import React, { useState, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { BellOff } from 'lucide-react';
import { Loader2 } from 'lucide-react';
import { useIFAlertas, useIFMarcarAlertaLido } from '../hooks/useIFAlertas';
import { useIFCarteiraResumo } from '../hooks/useIFCarteira';
import { getPrescricao } from '../services/prescricaoEngine';
import { IFAlertaPrescritivo } from '../components/IFAlertaPrescritivo';
import { PageHeader } from '@/components/system/PageHeader';
import { EmptyState } from '@/components/system/EmptyState';
import { FilterBar } from '@/components/system/FilterBar';

const ALLOWED_TIPOS = ['risco_elevado', 'prazo_critico', 'desvio_progresso', 'boletim_aprovado'];

const tipoLabels: Record<string, string> = {
  risco_elevado: 'Risco Elevado',
  prazo_critico: 'Prazo Crítico',
  desvio_progresso: 'Desvio de Progresso',
  boletim_aprovado: 'Boletins Aprovados',
};

const IFAlertasPage: React.FC = () => {
  const [searchParams] = useSearchParams();
  const filtroContratoId = searchParams.get('contrato_id');
  const { data: allAlertas = [], isLoading } = useIFAlertas();
  const { resumos } = useIFCarteiraResumo();
  const marcarLido = useIFMarcarAlertaLido();
  const [filtroTipo, setFiltroTipo] = useState<string>('todos');

  const resumoMap = useMemo(() => {
    const map = new Map(resumos.map(r => [r.contrato_id, r]));
    return map;
  }, [resumos]);

  const alertasPermitidos = useMemo(() => {
    let list = allAlertas.filter(a => ALLOWED_TIPOS.includes(a.tipo));
    if (filtroContratoId) list = list.filter(a => a.contrato_id === filtroContratoId);
    return list;
  }, [allAlertas, filtroContratoId]);

  const alertas = useMemo(() => {
    if (filtroTipo === 'todos') return alertasPermitidos;
    return alertasPermitidos.filter(a => a.tipo === filtroTipo);
  }, [alertasPermitidos, filtroTipo]);

  const tiposDisponiveis = useMemo(() => {
    const tipos = new Set(alertasPermitidos.map(a => a.tipo));
    return Array.from(tipos);
  }, [alertasPermitidos]);

  // Enrich with prescriptions and sort by impact
  const alertasComPrescricao = useMemo(() => {
    return alertas
      .map(a => {
        const resumo = resumoMap.get(a.contrato_id);
        const prescricao = getPrescricao(a, resumo);
        return { alerta: a, prescricao };
      })
      .sort((a, b) => b.prescricao.impactoMax - a.prescricao.impactoMax);
  }, [alertas, resumoMap]);

  if (isLoading) {
    return <div className="flex items-center justify-center py-16"><Loader2 className="h-8 w-8 animate-spin text-primary" /></div>;
  }

  const actionableCount = alertasComPrescricao.filter(a => a.prescricao.severidade !== 'info').length;

  const subtitleText = filtroTipo === 'todos'
    ? `${alertas.length} alerta${alertas.length !== 1 ? 's' : ''} · ${actionableCount} requerem ação`
    : `${alertas.length} de ${alertasPermitidos.length} — ${tipoLabels[filtroTipo] || filtroTipo}`;

  return (
    <div className="space-y-6">
      <PageHeader
        title="Alertas Prescritivos"
        subtitle={subtitleText}
      />

      <FilterBar>
        <Select value={filtroTipo} onValueChange={setFiltroTipo}>
          <SelectTrigger className="w-[200px] h-8 text-xs">
            <SelectValue placeholder="Filtrar por tipo" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="todos">Todos os tipos</SelectItem>
            {tiposDisponiveis.map(t => (
              <SelectItem key={t} value={t}>{tipoLabels[t] || t}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </FilterBar>

      {alertasComPrescricao.length === 0 ? (
        <EmptyState text="Nenhum alerta operacional ativo no momento." icon={BellOff} />
      ) : (
        <div className="space-y-3">
          {alertasComPrescricao.map(({ alerta, prescricao }) => (
            <IFAlertaPrescritivo
              key={alerta.id}
              alerta={alerta}
              prescricao={prescricao}
              onMarcarLido={(id) => marcarLido.mutate(id)}
              isPending={marcarLido.isPending}
            />
          ))}
        </div>
      )}
    </div>
  );
};

export default IFAlertasPage;
